import { FunctionComponent, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { OrganisationTag } from '../../../../../services';
import { getCssVar, IconLabel, PaginationInfo, useIsNarrowViewport } from '../../../../../shared';

import classNames from 'classnames';
import classes from './TagsTable.module.scss';

interface TagsTableProps {
  tags: OrganisationTag[];
  isFetching: boolean;
  isSearching: boolean;
  showFollowBehavior: boolean;
  allowToManage: boolean;
  onPageChange: (page: number) => void;
  onEdit: (tag: OrganisationTag) => void;
  onRemove: (tag: OrganisationTag) => void;
  pageInfo?: PaginationInfo;
}

export const TagsTable: FunctionComponent<TagsTableProps> = ({
  tags,
  isFetching,
  isSearching,
  showFollowBehavior,
  allowToManage,
  onPageChange,
  onEdit,
  onRemove,
  pageInfo,
}) => {
  const { t } = useTranslation();

  const isNarrowViewport = useIsNarrowViewport();

  const pagination = useMemo(() => {
    if (!pageInfo?.totalItems) {
      return null;
    }

    const { page, perPage, totalItems, hasNextPage, hasPreviousPage } = pageInfo;

    const from = (page - 1) * perPage + 1;

    const to = Math.min(page * perPage, totalItems);

    return (
      <div className={classes['tags-table__pagination']}>
        <IconLabel
          iconSize={20}
          iconId={'arrow-left'}
          ariaLabel={t('common.previous-page')}
          disabled={!hasPreviousPage}
          onClick={() => onPageChange(page - 1)}
          color={getCssVar('--base-link-text-color')}
          hoverColor={getCssVar('--base-link-text-hover-color')}
        />

        <span className={classes['tags-table__pagination-label']}>
          {from}-{to} {t('common.of')} {totalItems}
        </span>

        <IconLabel
          iconSize={20}
          iconId={'arrow-right'}
          ariaLabel={t('common.next-page')}
          disabled={!hasNextPage}
          onClick={() => onPageChange(page + 1)}
          color={getCssVar('--base-link-text-color')}
          hoverColor={getCssVar('--base-link-text-hover-color')}
        />
      </div>
    );
  }, [onPageChange, pageInfo, t]);

  if (isFetching) {
    return (
      <div className={classes['tags-table__loader']}>
        <Skeleton height={'3rem'} />
        <Skeleton height={'3rem'} />
        <Skeleton height={'3rem'} />
      </div>
    );
  }

  if (!tags.length) {
    return (
      <div className={classes['tags-table__wrapper']}>
        <div className={classes['tags-table__empty']}>
          {t(isSearching ? 'search.no-tags-found' : 'organisationTags.no-tags')}
        </div>
      </div>
    );
  }

  return (
    <>
      <div
        className={classNames(classes['tags-table__wrapper'], {
          [classes['tags-table__wrapper--narrow']]: isNarrowViewport,
        })}
      >
        <table className={classes['tags-table']}>
          <thead>
            <tr>
              <th>{t('common.name')}</th>
              {showFollowBehavior && <th>{t('common.feed-follow-behavior')}</th>}
              {allowToManage && <th />}
            </tr>
          </thead>
          <tbody>
            {tags.map((tag) => {
              const { id, name, settings } = tag;

              return (
                <tr key={id}>
                  <td>
                    <div className={classes['tags-table__name']} title={name}>
                      {name}
                    </div>
                  </td>

                  {showFollowBehavior && (
                    <td>
                      {settings?.feedFollowBehavior &&
                        t(`feedFollowBehavior.${settings.feedFollowBehavior}`)}
                    </td>
                  )}

                  {allowToManage && (
                    <td>
                      <div className={classes['tags-table__actions']}>
                        <IconLabel
                          iconId={'edit'}
                          iconSize={18}
                          ariaLabel={t('common.edit')}
                          color={'#888888'}
                          hoverColor={'#2d2d46'}
                          onClick={() => onEdit(tag)}
                        />
                        <IconLabel
                          iconId={'delete'}
                          iconSize={18}
                          ariaLabel={t('common.delete')}
                          color={'#888888'}
                          hoverColor={getCssVar('--color-danger-hover')}
                          onClick={() => onRemove(tag)}
                        />
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {pagination}
    </>
  );
};
